import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import {
    catchError,
    concatMap,
    debounceTime,
    filter,
    flatMap,
    map,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { AttachmentService } from '../../attachment-view-feature/attachment.service';
import * as fromLookups from '../../lookups';
import * as fromShared from '../../shared';
import { DisplayMessageMinimum, IStatusMessage } from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { LocaleEnum } from '../expense-enums';
import { ExpenseService } from '../expense.service';
import * as fromExpense from './index';

@Injectable()
export class ExpenseEffects {
    constructor(
        private actions$: Actions,
        private lookups: Store<fromLookups.LookupState>,
        private expenseStore: Store<fromExpense.State>,
        @Inject(fromLookups.CountryAreaLookupService)
        private countryAreaService: fromLookups.CountryAreaLookupService,
        @Inject(fromLookups.ExpenseItemLookupService)
        private expenseItemService: fromLookups.ExpenseItemLookupService,
        @Inject(fromLookups.PerDiemLookupService)
        private perDiemService: fromLookups.PerDiemLookupService,
        @Inject(fromLookups.LocaleLookupService)
        private localeService: fromLookups.LocaleLookupService,
        @Inject(fromLookups.TravelTypeLookupService)
        private travelTypeService: fromLookups.TravelTypeLookupService,
        @Inject(ExpenseService) private service: ExpenseService,
        @Inject(AttachmentService) private attachmentService: AttachmentService,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    updatePdbo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromExpense.UpdateExpenseLinePDBO>(
                fromExpense.ActionTypes.UpdateExpenseLinePDBO
            ),
            switchMap(a =>
                this.service.putExpensePdbo(a.pdbo).pipe(
                    map(resp => {
                        this.log.verbose({
                            type: 'ExpenseEffects:updatePdbo$',
                            resp
                        });
                        return new fromExpense.SetExpenseLinePDBO(resp);
                    })
                )
            )
        );
    });
    getPdbo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType<fromExpense.LoadExpenseLinePDBO>(
                fromExpense.ActionTypes.LoadExpenseLinePDBO
            ),
            switchMap(a =>
                this.service.getExpensePdbo(a.expenseLineId).pipe(
                    map(resp => {
                        this.log.verbose({
                            type: 'ExpenseEffects:updatePdbo$',
                            resp
                        });
                        return new fromExpense.SetExpenseLinePDBO(resp);
                    })
                )
            )
        );
    });
    SaveDetailAfterSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseLineFailure>(
                fromExpense.ActionTypes.AddExpenseLineFailure
            ),
            tap(x =>
                this.log.debug({
                    type: 'ExpenseEffects:SaveDetailAfterSummary$',
                    x
                })
            ),
            withLatestFrom(
                this.actions$.pipe(
                    ofType<fromExpense.AddExpenseDetails>(
                        fromExpense.ActionTypes.AddExpenseDetails
                    )
                )
            ),
            tap(x =>
                this.log.debug({
                    type: 'ExpenseEffects:SaveDetailAfterSummary$:AddExpenseSummary',
                    x
                })
            ),
            withLatestFrom(
                this.actions$.pipe(
                    ofType<fromExpense.AddExpenseSummarySuccess>(
                        fromExpense.ActionTypes.AddExpenseSummarySuccess
                    )
                )
            ),
            tap(x =>
                this.log.debug({
                    type: 'ExpenseEffects:SaveDetailAfterSummary$:AddExpenseSummary:AddExpenseSummarySuccess',
                    x
                })
            ),
            filter(([[lf, d], sx]) => d.payload.summary.ExpReportNbr === -1),
            tap(x =>
                this.log.debug({
                    type: 'ExpenseEffects:SaveDetailAfterSummary$:filtered',
                    x
                })
            ),
            map(
                ([[lf, d], sx]) =>
                    new fromExpense.ClearExpenseNumber(
                        sx.payload.Items[0].ExpReportNbr
                    )
            )
        )
    );

    LoadLocations$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadLocations>(
                fromLookups.LookupActionTypes.LoadLocations
            ),
            switchMap(a =>
                this.countryAreaService
                    .getLocations(a.payload.locale, a.payload.countryArea)
                    .pipe(
                        map(x => new fromLookups.SetLocations(x)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    Areas$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadAreas>(
                fromLookups.LookupActionTypes.LoadAreas
            ),
            withLatestFrom(this.lookups.pipe(select(fromLookups.getAreas))),
            filter(([, items]) => items.length === 0),
            switchMap(() =>
                this.countryAreaService.get(LocaleEnum.OCONUS).pipe(
                    map(x => new fromLookups.SetAreas(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    Countries$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadCountries>(
                fromLookups.LookupActionTypes.LoadCountries
            ),
            withLatestFrom(this.lookups.pipe(select(fromLookups.getCountries))),
            filter(([, items]) => items.length === 0),
            switchMap(a =>
                this.countryAreaService.get(LocaleEnum.Foreign).pipe(
                    map(x => new fromLookups.SetCountries(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    ExpenseItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadExpenseItems>(
                fromLookups.LookupActionTypes.LoadExpenseItems
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromLookups.getTravelTypesLookup))
            ),
            filter(([, items]) => items.length === 0),
            switchMap(a =>
                this.expenseItemService.get().pipe(
                    map(x => new fromLookups.SetExpenseItems(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    TravelTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadTravelTypes>(
                fromLookups.LookupActionTypes.LoadTravelTypes
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromLookups.getTravelTypesLookup))
            ),
            filter(([, travelTypes]) => travelTypes.length === 0),
            switchMap(a =>
                this.travelTypeService.get().pipe(
                    map(x => new fromLookups.SetTravelTypes(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    Locales$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadLocales>(
                fromLookups.LookupActionTypes.LoadLocales
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromLookups.getLocalesLookup))
            ),
            filter(([, locales]) => locales.length === 0),
            switchMap(a =>
                this.localeService.get().pipe(
                    map(x => new fromLookups.SetLocales(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    PerDiemTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromLookups.LoadPerDiemTypes>(
                fromLookups.LookupActionTypes.LoadPerDiemTypes
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromLookups.getPerDiemTypesLookup))
            ),
            filter(([, perDiemTypes]) => perDiemTypes.length === 0),
            switchMap(a =>
                this.perDiemService.get().pipe(
                    map(x => new fromLookups.SetPerDiemTypes(x)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadExpenseSummaries$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseSummaries>(
                fromExpense.ActionTypes.LoadExpenseSummaries
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadExpenses$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getExpenseSummaries(x.payload).pipe(
                    map(exp => new fromExpense.SetExpenseSummaries(exp)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadExpenseSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseSummary>(
                fromExpense.ActionTypes.LoadExpenseSummary
            ),
            switchMap(action =>
                this.service
                    .getExpenseSummary(
                        action.payload.expenseNbr,
                        action.payload.EmployeeListID
                    )
                    .pipe(
                        map(x => new fromExpense.UpdateExpenseSummary(x)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    DeleteExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.DeleteExpenseSummary>(
                fromExpense.ActionTypes.DeleteExpenseSummary
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:DeleteExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.deleteExpense(x.payload).pipe(
                    concatMap(exp =>
                        exp
                            ? [
                                  new fromExpense.RemoveExpenseSummary(exp),
                                  new fromExpense.ResetSelectedExpense()
                              ]
                            : [fromShared.Errors.UnknownResponse()]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    DeleteExpenseDetail$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.DeleteExpenseDetail>(
                fromExpense.ActionTypes.DeleteExpenseDetail
            ),
            withLatestFrom(
                this.actions$.pipe(
                    ofType<fromExpense.LoadExpenseSummary>(
                        fromExpense.ActionTypes.LoadExpenseSummary
                    )
                )
            ),
            tap(actions =>
                this.log.debug({
                    type: 'ExpenseEffects:DeleteExpenseDetail$:filtered',
                    actions
                })
            ),
            switchMap(([deleteAction, loadSummaryAction]) =>
                this.service.deleteExpenseDetail(deleteAction.payload).pipe(
                    concatMap(
                        exp =>
                            exp
                                ? [
                                      new fromExpense.RemoveExpenseDetail(exp),
                                      new fromExpense.LoadExpenseSummary({
                                          expenseNbr: exp.ExpReportNbr,
                                          EmployeeListID:
                                              loadSummaryAction.payload
                                                  .EmployeeListID
                                      })
                                  ]
                                : [fromShared.Errors.UnknownResponse()]
                        // new fromExpense.ResetSelectedExpense()
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    DeleteExpenseReceipt$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.DeleteExpenseReceipt>(
                fromExpense.ActionTypes.DeleteExpenseReceipt
            ),
            withLatestFrom(
                this.actions$.pipe(
                    ofType<fromExpense.LoadExpenseSummary>(
                        fromExpense.ActionTypes.LoadExpenseSummary
                    )
                )
            ),
            tap(actions =>
                this.log.debug({
                    type: 'ExpenseEffects:DeleteExpenseReceipt$:filtered',
                    actions
                })
            ),
            switchMap(([deleteReceiptAction, loadSummaryAction]) =>
                // switchMap(deleteReceiptAction =>
                this.service
                    .deleteExpenseReceipt(deleteReceiptAction.payload)
                    .pipe(
                        concatMap(resp =>
                            fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                                ? [
                                      new fromExpense.RemoveExpenseReceipt(
                                          resp
                                      ),
                                      new fromExpense.LoadExpenseLines(
                                          deleteReceiptAction.payload.ExpenseRptNbr
                                      ),
                                      new fromExpense.LoadExpenseSummary({
                                          expenseNbr:
                                              deleteReceiptAction.payload
                                                  .ExpenseRptNbr,
                                          EmployeeListID: loadSummaryAction
                                              ? loadSummaryAction.payload
                                                    .EmployeeListID
                                              : null
                                      })
                                  ]
                                : [
                                      new fromShared.Errors.SetErrors(
                                          fromShared.Errors.MapErrorsToStatus(
                                              resp
                                          )
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    OversignExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.OversignExpenseSummary>(
                fromExpense.ActionTypes.OversignExpenseSummary
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:OversignExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.oversignExpense(x.payload).pipe(
                    concatMap(exp =>
                        exp.Items
                            ? exp.Items.map(
                                  e => new fromExpense.UpdateExpenseSummary(e)
                              )
                            : [
                                  fromShared.Errors.handleNullDataError(
                                      exp.Items,
                                      'Oversign'
                                  )
                              ]
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    ConvertExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.ConvertExpense>(
                fromExpense.ActionTypes.ConvertExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:ConvertExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .convertExpense(x.payload.summary, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Convert'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    ValidateExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.ValidateExpense>(
                fromExpense.ActionTypes.ValidateExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:ValidateExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .validateExpense(x.payload.summaries, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Validate'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );

    UnvalidateExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.UnvalidateExpense>(
                fromExpense.ActionTypes.UnvalidateExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:UnvalidateExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .unvalidateExpense(x.payload.summaries, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Unvalidate'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    ApproveExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.ApproveExpense>(
                fromExpense.ActionTypes.ApproveExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:ApproveExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .approveExpense(x.payload.summaries, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Approve'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    UnapproveExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.UnapproveExpense>(
                fromExpense.ActionTypes.UnapproveExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:UnapproveExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .unapproveExpense(x.payload.summaries, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Unapprove'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    DisapproveExpense$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.DisapproveExpense>(
                fromExpense.ActionTypes.DisapproveExpense
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:DisapproveExpense$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .disapproveExpense(x.payload.summaries, x.payload.roleId)
                    .pipe(
                        concatMap(exp =>
                            exp.Items
                                ? exp.Items.map(
                                      e =>
                                          new fromExpense.UpdateExpenseSummary(
                                              e
                                          )
                                  )
                                : [
                                      fromShared.Errors.handleNullDataError(
                                          exp.Items,
                                          'Disapprove'
                                      )
                                  ]
                        ),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    SelectedExpenseClear$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                fromExpense.ActionTypes.ValidateExpense,
                fromExpense.ActionTypes.UnvalidateExpense,
                fromExpense.ActionTypes.ApproveExpense,
                fromExpense.ActionTypes.UnapproveExpense,
                fromExpense.ActionTypes.DisapproveExpense
            ),
            map(x => new fromExpense.ClearSelectedExpenses()),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    LoadExpenseLines$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseLines>(
                fromExpense.ActionTypes.LoadExpenseLines
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadExpenseLines$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getExpenseLines(x.payload).pipe(
                    tap(receipts =>
                        this.log.debug({
                            type: 'ExpenseEffects:getExpenseLines',
                            receipts
                        })
                    ),
                    map(
                        lineItems => new fromExpense.SetExpenseLines(lineItems)
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadExpenseReceipts$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseReceipts>(
                fromExpense.ActionTypes.LoadExpenseReceipts
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadExpenseReceipts$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getExpenseReceipts(x && x.payload).pipe(
                    tap(receipts =>
                        this.log.debug({
                            type: 'ExpenseEffects:getExpenseReceipts',
                            receipts
                        })
                    ),
                    map(
                        receipts => new fromExpense.SetExpenseReceipts(receipts)
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadExpensePaymentMethods$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadPaymentMethods>(
                fromExpense.ActionTypes.LoadPaymentMethods
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadExpensePaymentMethods$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service.getPaymentMethods(action.payload).pipe(
                    tap(paymentMethods =>
                        this.log.debug({
                            type: 'ExpenseEffects:getPaymentMethods',
                            paymentMethods
                        })
                    ),
                    map(
                        paymentMethods =>
                            new fromExpense.SetPaymentMethods(paymentMethods)
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadUserPaymentMethods$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadUserPaymentMethods>(
                fromExpense.ActionTypes.LoadUserPaymentMethods
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadUserPaymentMethods$:filtered',
                    action
                })
            ),
            debounceTime(500),
            switchMap(action =>
                this.service.getUserPaymentMethods(action.payload).pipe(
                    tap(paymentMethods =>
                        this.log.debug({
                            type: 'ExpenseEffects:getUserPaymentMethods',
                            paymentMethods
                        })
                    ),
                    map(
                        paymentMethods =>
                            new fromExpense.SetUserPaymentMethods(
                                paymentMethods
                            )
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadTravelMode$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadPaymentMethods>(
                fromExpense.ActionTypes.LoadTravelMode
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromExpense.getExpenseTravelModes))
            ),
            filter(([, items]) => items.length === 0),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadTravelMode$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service.getTravelModes().pipe(
                    tap(travelModes =>
                        this.log.debug({
                            type: 'ExpenseEffects:LoadTravelMode',
                            travelModes
                        })
                    ),
                    map(
                        travelModes =>
                            new fromExpense.SetTravelMode(travelModes)
                    ),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    AddExpenseSummary$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseSummary>(
                fromExpense.ActionTypes.AddExpenseSummary
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseSummary$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service.saveExpenseSummary(action.payload).pipe(
                    tap(summary =>
                        this.log.debug({
                            type: 'ExpenseEffects:saveExpenseSummary',
                            summary
                        })
                    ),
                    concatMap(summary => {
                        if (
                            fromShared.Errors.IsSuccessStatus(
                                summary.StatusCode
                            )
                        ) {
                            return [
                                new fromExpense.AddExpenseSummarySuccess(
                                    summary
                                ),
                                new fromExpense.EditExpenseSummary(
                                    summary.Items[0]
                                )
                            ];
                        } else {
                            return [
                                new fromExpense.AddExpenseSummaryFailure(
                                    summary
                                )
                            ];
                        }
                    }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    AddExpenseSummaryFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseSummaryFailure>(
                fromExpense.ActionTypes.AddExpenseSummaryFailure
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseSummaryFailure$:filtered',
                    action
                })
            ),
            map(
                err =>
                    new fromShared.Errors.SetErrors(
                        [
                            ...err.payload.ErrorMessages.map(
                                x =>
                                    ({
                                        StatusCode: -1,
                                        Message: x,
                                        MsgProcessID: 0,
                                        MsgEntityID: ''
                                    } as IStatusMessage)
                            ),
                            ...err.payload.Items.filter(
                                x =>
                                    x &&
                                    x.Status.StatusCode >= DisplayMessageMinimum
                            ).map(x => x.Status)
                        ].filter(fromShared.Errors.filterErrors)
                    )
            ),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    AddExpenseLine$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseLine>(
                fromExpense.ActionTypes.AddExpenseLine
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseLine$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service
                    .saveExpenseDetail({
                        ...action.payload.line,
                        ExpReportNbr:
                            action.payload.summary.Items[0].ExpReportNbr,
                        WorkDate: new Date(
                            action.payload.summary.Items[0].ReportDate
                        )
                    })
                    .pipe(
                        tap(resp =>
                            this.log.debug({
                                type: 'ExpenseEffects:AddExpenseLine$',
                                resp
                            })
                        ),
                        concatMap(resp => {
                            if (
                                fromShared.Errors.IsSuccessStatus(
                                    resp.StatusCode
                                )
                            ) {
                                return [
                                    new fromExpense.AddExpenseLineSuccess(resp),
                                    new fromExpense.AddExpenseReceipts(
                                        resp.Items[0],
                                        action.payload.line.Receipts
                                    )
                                ];
                            } else {
                                return [
                                    new fromExpense.AddExpenseLineFailure(resp)
                                ];
                            }
                        }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    AddExpenseLineSuccess$ = createEffect(() =>
        combineLatest([
            this.actions$.pipe(
                ofType<fromExpense.AddExpenseLineSuccess>(
                    fromExpense.ActionTypes.AddExpenseLineSuccess
                )
            ),
            this.actions$.pipe(
                ofType<fromExpense.AddExpenseSummarySuccess>(
                    fromExpense.ActionTypes.AddExpenseSummarySuccess
                )
            ),
            this.expenseStore.pipe(
                select(fromExpense.getPostSaveDetailNavigation)
            )
        ]).pipe(
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseLinesSuccess$:filtered',
                    action
                })
            ),
            concatMap(
                ([lineSuccessAction, summarySuccessAction, postSaveNav]) => {
                    const summaryLoad = new fromExpense.LoadExpenseSummary({
                        expenseNbr:
                            lineSuccessAction.payload.Items[0].ExpReportNbr,
                        EmployeeListID:
                            summarySuccessAction.payload.Items[0]
                                .EmployeeListID
                    });
                    if (postSaveNav?.mode === 'new' || postSaveNav?.mode === 'copy') {
                        return [
                            summaryLoad,
                            new fromExpense.SetPostSaveDetailNavigation(null)
                        ];
                    }
                    return [
                        new fromExpense.EditExpenseDetail(
                            lineSuccessAction.payload.Items[0]
                        ),
                        summaryLoad
                    ];
                }
            ),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    AddExpenseLinesFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseLineFailure>(
                fromExpense.ActionTypes.AddExpenseLineFailure
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseLinesFailure$:filtered',
                    action
                })
            ),
            map(
                err =>
                    new fromShared.Errors.SetErrors(
                        fromShared.Errors.MapErrorsToStatus(err.payload)
                    )
            ),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    AddExpenseDetails$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseDetails>(
                fromExpense.ActionTypes.AddExpenseDetails
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseDetail$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service.saveExpenseSummary(action.payload.summary).pipe(
                    flatMap((resp): Action[] => {
                        if (
                            fromShared.Errors.IsSuccessStatus(resp.StatusCode)
                        ) {
                            return [
                                ...action.payload.details.map(
                                    d =>
                                        new fromExpense.AddExpenseLine({
                                            summary: resp,
                                            line: d
                                        })
                                ),
                                new fromExpense.AddExpenseSummarySuccess({
                                    ...resp,
                                    Items: resp.Items.map(summary => ({
                                        ...summary,
                                        EmployeeListID:
                                            action.payload.summary
                                                .EmployeeListID
                                    }))
                                })
                            ];
                        } else {
                            return [
                                new fromExpense.AddExpenseSummaryFailure(resp)
                            ];
                        }
                    }),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    AddExpenseReceipts$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseReceipts>(
                fromExpense.ActionTypes.AddExpenseReceipts
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseReceipts$:filtered',
                    action
                })
            ),
            map(action => ({
                ...action,
                payload: action.payload.filter(x => x.Creditaccountlistid)
            })),
            filter(action => action.payload.length > 0),
            switchMap(action =>
                this.service
                    .saveExpenseReceipt(
                        action.payload
                            .filter(r => r.Creditaccountlistid)
                            .map(r => ({
                                ...r,
                                ExpenseID: action.detail.ExpenseID
                            }))
                    )
                    .pipe(
                        tap(resp =>
                            this.log.debug({
                                type: 'ExpenseEffects:AddExpenseReceipt$:response',
                                resp
                            })
                        ),
                        flatMap(resp => {
                            if (
                                fromShared.Errors.IsSuccessStatus(
                                    resp.StatusCode
                                )
                            ) {
                                return [
                                    new fromExpense.AddExpenseReceiptsSuccess(
                                        resp
                                    )
                                ];
                            } else {
                                return [
                                    new fromExpense.AddExpenseReceiptsFailure(
                                        resp
                                    )
                                ];
                            }
                        }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    AddExpenseReceiptsSuccess$ = createEffect(() =>
        combineLatest([
            this.actions$.pipe(
                ofType<fromExpense.AddExpenseReceiptsSuccess>(
                    fromExpense.ActionTypes.AddExpenseReceiptsSuccess
                )
            ),
            this.actions$.pipe(
                ofType<fromExpense.AddExpenseSummarySuccess>(
                    fromExpense.ActionTypes.AddExpenseSummarySuccess
                )
            )
        ]).pipe(
            concatMap(([receiptSuccessAction, summarySuccessAction]) => [
                new fromExpense.LoadExpenseLines(
                    receiptSuccessAction.payload.Items[0].ExpenseRptNbr
                ),
                new fromExpense.LoadExpenseSummary({
                    expenseNbr:
                        receiptSuccessAction.payload.Items[0].ExpenseRptNbr,
                    EmployeeListID:
                        summarySuccessAction.payload.Items[0].EmployeeListID
                })
            ])
        )
    );
    AddExpenseReceiptsFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseReceiptsFailure>(
                fromExpense.ActionTypes.AddExpenseLineFailure
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseReceiptsFailure$:filtered',
                    action
                })
            ),
            map(
                err =>
                    new fromShared.Errors.SetErrors(
                        [
                            ...err.payload.ErrorMessages.map(
                                x =>
                                    ({
                                        StatusCode: -1,
                                        Message: x,
                                        MsgProcessID: 0,
                                        MsgEntityID: ''
                                    } as IStatusMessage)
                            ),
                            ...err.payload.Items.filter(
                                x =>
                                    x &&
                                    x.Status.StatusCode >= DisplayMessageMinimum
                            ).map(x => x.Status)
                        ].filter(fromShared.Errors.filterErrors)
                    )
            ),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    LoadReceiptImageFailure$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadReceiptImageFailure>(
                fromExpense.ActionTypes.LoadReceiptImageFailure
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadReceiptImageFailure$:filtered',
                    action
                })
            ),
            map(
                err =>
                    new fromShared.Errors.SetErrors(
                        [err.payload].map(
                            x =>
                                ({
                                    StatusCode: -1,
                                    Message: x,
                                    MsgProcessID: 0,
                                    MsgEntityID: ''
                                } as IStatusMessage)
                        )
                    )
            ),
            catchError(fromShared.Errors.handleServiceError)
        )
    );
    SelectDirectReport$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.SelectDirectReport>(
                fromExpense.ActionTypes.SelectDirectReport
            ),
            flatMap(action => [
                new fromExpense.SetExpenseTasks([]),
                new fromExpense.LoadExpenseTasks(action.payload.EmployeeListID)
            ])
        )
    );
    LoadExpenseTasks$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseTasks>(
                fromExpense.ActionTypes.LoadExpenseTasks
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromExpense.getExpenseTasks))
            ),
            filter(([, tasks]) => tasks.length === 0),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadExpenseTasks$:filtered',
                    action
                })
            ),
            switchMap(([action]) =>
                this.service.getExpenseTasks(action.payload).pipe(
                    tap(tasks =>
                        this.log.debug({
                            type: 'ExpenseEffects:loadExpenseTasks',
                            tasks
                        })
                    ),
                    map(tasks => new fromExpense.SetExpenseTasks(tasks)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    LoadTravelRates$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadTravelRates>(
                fromExpense.ActionTypes.LoadTravelRates
            ),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadTravelRates$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service
                    .getTravelRates(action.payload) // want cancellable, only interested in last call results
                    .pipe(
                        tap(rates =>
                            this.log.debug({
                                type: 'ExpenseEffects:getTravelRates',
                                rates
                            })
                        ),
                        map(rates => new fromExpense.SetTravelRates(rates)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    LoadTripLegTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadTripLegTypes>(
                fromExpense.ActionTypes.LoadTripLegTypes
            ),
            withLatestFrom(
                this.lookups.pipe(select(fromExpense.getTripLegTypes))
            ),
            filter(([, items]) => items.length === 0),
            tap(action =>
                this.log.debug({
                    type: 'ExpenseEffects:LoadTripLegTypes$:filtered',
                    action
                })
            ),
            switchMap(action =>
                this.service.getTripLegTypes().pipe(
                    tap(types =>
                        this.log.debug({
                            type: 'ExpenseEffects:getTravelRates',
                            types
                        })
                    ),
                    map(types => new fromExpense.SetTripLegTypes(types)),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );
    AddExpenseAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseAttachment>(
                fromExpense.ActionTypes.AddExpenseAttachment
            ),
            withLatestFrom(
                this.expenseStore.pipe(
                    select(fromExpense.getEditingExpenseReceipt)
                )
            ),
            tap(x =>
                this.log.debug({
                    type: 'ExpenseEffects:AddExpenseAttachment$',
                    x
                })
            ),
            switchMap(([action, receipt]) =>
                this.attachmentService
                    .saveExpenseAttachment(
                        action.expenseNumber,
                        action.uniqueId,
                        receipt && receipt.File_unique_id,
                        action.meta,
                        action.file
                    )
                    .pipe(
                        tap(res =>
                            this.log.debug({
                                type: 'ExpenseEffects:addExpenseAttachment',
                                res
                            })
                        ),
                        map(res => {
                            if (
                                fromShared.Errors.IsSuccessStatus(
                                    res.StatusCode
                                )
                            ) {
                                return new fromExpense.AddExpenseAttachmentSuccess(
                                    res
                                );
                            } else {
                                return new fromExpense.AddExpenseAttachmentFailure(
                                    res
                                );
                            }
                        }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    EditExpenseReceipt$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.EditExpenseReceipt>(
                fromExpense.ActionTypes.EditExpenseReceipt
            ),
            map(
                action =>
                    new fromExpense.LoadExpenseReceiptAttachment(action.payload)
            )
        )
    );
    LoadExpenseReceiptAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseReceiptAttachment>(
                fromExpense.ActionTypes.LoadExpenseReceiptAttachment
            ),
            switchMap(action =>
                this.attachmentService
                    .getExpenseReceiptAttachment(action.payload)
                    .pipe(
                        map(x => {
                            return new fromExpense.EditExpenseReceiptAttachment(
                                x
                            );
                        }),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
    AddExpenseAttachmentSuccess$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.AddExpenseAttachmentSuccess>(
                fromExpense.ActionTypes.AddExpenseAttachmentSuccess
            ),
            withLatestFrom(
                this.expenseStore.pipe(
                    select(fromExpense.getEditingExpenseReceipt)
                )
            ),
            withLatestFrom(
                this.actions$.pipe(
                    ofType<fromExpense.LoadExpenseSummary>(
                        fromExpense.ActionTypes.LoadExpenseSummary
                    )
                )
            ),
            flatMap(([[action, receipt], loadSummaryAction]) => [
                new fromExpense.LoadExpenseSummary({
                    expenseNbr: receipt.ExpenseRptNbr,
                    EmployeeListID: loadSummaryAction.payload.EmployeeListID
                }),
                new fromExpense.LoadExpenseLines(receipt.ExpenseRptNbr),
                new fromExpense.LoadExpenseReceipts(receipt.ExpenseRptNbr)
            ])
        )
    );
    LoadExpenseReports$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromExpense.LoadExpenseReports>(
                fromExpense.ActionTypes.LoadExpenseReports
            ),
            debounceTime(500),
            switchMap(action =>
                this.service
                    .getDirectReports(
                        action.roleId,
                        action.activeOnly,
                        action.startDate,
                        action.endDate
                    )
                    .pipe(
                        map(data => new fromExpense.SetExpenseReports(data)),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );
}
